import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Alert, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { omit } from 'lodash';
import moment from 'moment';
import { usePostHog } from 'posthog-js/react';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService, EventScheduleData, EventScheduleSlotData, OrderPageData } from '../../../api';
import { useLanguage } from '../../../common/GeneralUtils';
import { getEffectivePageLocationId } from '../../../common/filter/locationGrouping';
import { OrderPageUpdate } from '../../OrderPageSettingsPage';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import { EventSchedule, EventScheduleFormValues } from '../../eventSchedule/EventSchedule';
import { EventSchedulePresetsPanel } from '../../eventSchedule/EventSchedulePresetsPanel';
import { SectionSaveRegistrar } from '../../saveHeader/SectionSaveRegistrar';
import { diffEventSchedule } from '../../saveHeader/diffBuilders';
import { SettingsPageTabs } from '../../SettingsPageTabs';
import { FormDatePicker } from '../../formComponents/FormDatePicker';

type EventScheduleSectionProps = {
  orderPage: OrderPageData;
  orderPageInitialValues: OrderPageUpdate;
  updateLocalAccount: KeyedMutator<AccountData>;
  account: AccountData;
};

const DAYS: (keyof EventScheduleData)[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const emptyEventSchedule = (): EventScheduleData => ({
  sunday: [],
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
});

const slotSchema = yup.object({
  start_time: yup
    .number()
    .integer()
    .min(0, 'invalidSlotStartTime')
    .lessThan(86400, 'invalidSlotStartTime')
    .required('invalidSlotStartTime'),
  employee_ids: yup.array().of(yup.string()).optional(),
});

const daySchema = yup
  .array()
  .of(slotSchema)
  .test('unique-start-times', 'duplicateSlotStartTime', (slots) => {
    if (!slots) return true;
    const times = (slots as EventScheduleSlotData[]).map((s) => s?.start_time).filter((t): t is number => t != null);
    return new Set(times).size === times.length;
  });

export const EventScheduleSection = ({
  orderPage,
  orderPageInitialValues,
  updateLocalAccount,
  account,
}: EventScheduleSectionProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const activity =
    orderPage.activity_label?.[language] ||
    account.activity_label_singular?.[language] ||
    t('editOrderPage.activityFallback');
  const { readOnly } = UseAuthPermissions();
  const posthog = usePostHog();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const todayInAccountTz = moment.tz(account.timezone).format('YYYY-MM-DD');
  const minApplyFrom = moment.tz(account.timezone).subtract(1, 'day').format('YYYY-MM-DD');

  const validationSchema = yup.object().shape({
    event_schedule: yup.object().shape(
      DAYS.reduce<Record<string, typeof daySchema>>((acc, d) => {
        acc[d] = daySchema;
        return acc;
      }, {}),
    ),
    event_schedule_apply_from: yup
      .string()
      .matches(/^\d{4}-\d{2}-\d{2}$/, 'invalidApplyFromDate')
      .test('not-in-past', 'invalidApplyFromDate', (v) => {
        if (!v) return false;
        return moment(v).isSameOrAfter(moment(minApplyFrom));
      })
      .required('invalidApplyFromDate'),
  });

  const initialValues: EventScheduleFormValues = {
    event_schedule: orderPage.event_schedule ?? emptyEventSchedule(),
    event_schedule_apply_from: todayInAccountTz,
  };

  const handleSubmit = async (
    values: EventScheduleFormValues,
    { setStatus }: FormikHelpers<EventScheduleFormValues>,
  ) => {
    try {
      posthog.capture('Editing settings', { section: 'eventScheduleSection', settings: values });
      const updatedAccount = await AccountService.updateOrderPage(
        omit(
          {
            ...orderPageInitialValues,
            duration: (orderPageInitialValues.duration || 0) * 60,
            event_schedule: values.event_schedule,
            event_schedule_apply_from: values.event_schedule_apply_from,
            id: orderPage.id,
          },
          'availability_settings.restrictions',
          'objects',
        ),
      );
      await updateLocalAccount(updatedAccount);
    } catch (e) {
      setStatus({ error: (e as Error)?.message || t('saveHeader.errorGeneric') });
      throw e;
    }
  };

  const getChanges = (values: EventScheduleFormValues, iv: EventScheduleFormValues) =>
    diffEventSchedule(iv.event_schedule, values.event_schedule, account.users ?? [], t);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {(formik) => {
        const { values, isSubmitting, setFieldValue } = formik;
        return (
          <Form onSubmit={formik.handleSubmit}>
            <SectionSaveRegistrar
              tabKey={SettingsPageTabs.EVENT_SCHEDULE}
              getChanges={getChanges}
              disabled={readOnly}
            />
            <Grid container flexDirection="column" gap={3} flexWrap="nowrap" alignItems="flex-start">
              <Grid item>
                <Typography variant="h6" fontWeight={700} mb={1}>
                  {t('eventSchedule.title', { activity })}
                </Typography>
                <Typography variant="body2" color="text.secondary" maxWidth={640}>
                  {t('eventSchedule.description', { activity })}
                </Typography>
              </Grid>
              <EventSchedulePresetsPanel orderPage={orderPage} />
              <Grid
                container
                flexDirection={isSmallScreen ? 'column' : 'row'}
                gap={2}
                alignItems={isSmallScreen ? 'stretch' : 'center'}>
                <FormDatePicker
                  fieldName="event_schedule_apply_from"
                  label={t('eventSchedule.applyFrom')}
                  size="small"
                  disablePast={false}
                  isFieldUpdate
                />
                <Typography variant="body2" color="text.secondary" maxWidth={480}>
                  {t('eventSchedule.applyFromHelp')}
                </Typography>
              </Grid>
              <Alert severity="info" sx={{ width: '100%', maxWidth: 720 }}>
                {t('eventSchedule.grandfatherNote')}
              </Alert>
              <EventSchedule
                eventSchedule={values.event_schedule}
                setFieldValue={setFieldValue}
                isSubmitting={isSubmitting}
                readOnly={readOnly}
                users={account.users ?? []}
                locationId={getEffectivePageLocationId(orderPage.location_id, account)}
              />
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
